import {
  AllCellRenderers,
  ImageWindowLoaderImpl,
  markerCellRenderer,
  type GridSelection,
  type Rectangle,
} from "@glideapps/glide-data-grid";
import { useCallback, useMemo, useRef } from "react";
import { useResolvedTheme } from "@volca/ui/theme";
import { drawSelectedRowMarkerBackground } from "./cell-markers";
import { getSavedSpreadsheetRow } from "./spreadsheet-region-view";
import { buildSpreadsheetTheme, volcaColor } from "./spreadsheet-theme";

interface UseGridRenderersParams {
  gridSelection: GridSelection;
  hasActiveRegionView: boolean;
  regionRowMap: number[];
}

export const useGridRenderers = ({
  gridSelection,
  hasActiveRegionView,
  regionRowMap,
}: UseGridRenderersParams) => {
  const gridImageWindowLoader = useMemo(() => new ImageWindowLoaderImpl(), []);

  const appearance = useResolvedTheme();
  const gridTheme = useMemo(
    () => buildSpreadsheetTheme(appearance),
    [appearance],
  );

  const selectedRowRef = useRef<number | null>(null);
  selectedRowRef.current = gridSelection.current?.cell[1] ?? null;
  const selectedRowsRef = useRef<GridSelection["rows"] | null>(null);
  selectedRowsRef.current = gridSelection.rows;
  const selectedRangesRef = useRef<readonly Rectangle[]>([]);
  selectedRangesRef.current = gridSelection.current
    ? [gridSelection.current.range, ...gridSelection.current.rangeStack]
    : [];

  const isColumnInSelectedRange = useCallback(
    (columnIndex: number) =>
      selectedRangesRef.current.some(
        (range) =>
          columnIndex >= range.x && columnIndex < range.x + range.width,
      ),
    [],
  );
  const isRowInSelectedRange = useCallback(
    (rowIndex: number) =>
      selectedRangesRef.current.some(
        (range) => rowIndex >= range.y && rowIndex < range.y + range.height,
      ),
    [],
  );

  const gridRenderers = useMemo(() => {
    const rowMarkerRenderer = {
      ...markerCellRenderer,
      ...(hasActiveRegionView
        ? {
            getAccessibilityString: (
              cell: Parameters<
                NonNullable<typeof markerCellRenderer.getAccessibilityString>
              >[0],
            ) =>
              String(
                getSavedSpreadsheetRow(
                  regionRowMap,
                  Math.max(0, cell.row - 1),
                ) + 1,
              ),
          }
        : {}),
      draw: (args: Parameters<typeof markerCellRenderer.draw>[0]) => {
        const isWholeRowSelected =
          selectedRowsRef.current?.hasIndex(args.row) ?? false;
        const isSelectedRow =
          isWholeRowSelected ||
          isRowInSelectedRange(args.row) ||
          args.row === selectedRowRef.current;
        if (isSelectedRow) {
          drawSelectedRowMarkerBackground(
            args.ctx,
            args.rect,
            isWholeRowSelected,
          );
        }

        const savedRow = hasActiveRegionView ? regionRowMap[args.row] : null;
        return markerCellRenderer.draw(
          {
            ...args,
            ...(isSelectedRow
              ? {
                  theme: {
                    ...args.theme,
                    textLight: volcaColor("--ink"),
                  },
                }
              : {}),
            ...(savedRow == null
              ? {}
              : { cell: { ...args.cell, row: savedRow + 1 } }),
          },
          args.cell,
        );
      },
    } as (typeof AllCellRenderers)[number];

    return AllCellRenderers.map((renderer) =>
      renderer.kind === markerCellRenderer.kind ? rowMarkerRenderer : renderer,
    );
  }, [hasActiveRegionView, isRowInSelectedRange, regionRowMap]);

  return {
    gridImageWindowLoader,
    gridTheme,
    isColumnInSelectedRange,
    gridRenderers,
  };
};
